import type { OrgAskResponse } from '@/lib/api';
import type { StreamProgress } from '@/lib/orgAskStream';

/**
 * Shared conversation entry structure for Ask Hobbes.
 * Used by both the side rail and full page views.
 */
export interface AskHobbesEntry {
  id: string;
  prompt: string;
  createdAt: Date;
  status: 'loading' | 'success' | 'error';
  response?: OrgAskResponse;
  progress?: StreamProgress;
  errorMessage?: string;
}

/**
 * Organization-scoped conversation cache shared between
 * the Ask Hobbes side rail and full page view.
 */
export const askHobbesCache = new Map<string, AskHobbesEntry[]>();

/**
 * Get cached entries for an organization.
 */
export const getAskHobbesCache = (orgId: string): AskHobbesEntry[] | undefined => {
  return askHobbesCache.get(orgId);
};

/**
 * Set cached entries for an organization.
 */
export const setAskHobbesCache = (orgId: string, entries: AskHobbesEntry[]): void => {
  askHobbesCache.set(orgId, entries);
};

/**
 * Clear cached entries for an organization.
 */
export const clearAskHobbesCache = (orgId: string): void => {
  askHobbesCache.delete(orgId);
};

/**
 * Clone entries for safe state updates (deep copy dates and nested objects).
 */
export const cloneEntries = (entries: AskHobbesEntry[]): AskHobbesEntry[] => {
  return entries.map((entry) => ({
    ...entry,
    createdAt: new Date(entry.createdAt),
    response: entry.response
      ? {
          ...entry.response,
          evidence: entry.response.evidence?.map((item) => ({ ...item })),
        }
      : undefined,
    progress: entry.progress ? { ...entry.progress } : undefined,
  }));
};
